import styles from 'css/src/components/common/ui/feature-lock-modal.module.scss';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { RoleEnum, Urls } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { BUTTON_VARIANTS, PrimaryButton } from 'js/src/components/common/ui/Button';
import FeatureLockInlinePlan from 'js/src/components/common/ui/feature-lock-inline-plan';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import type { FeatureLockModalContext } from 'js/src/data/modals';
import {
    getModalContext,
    hideModal,
    isFeatureLockModalShowing,
    LOCKED_FEATURES,
} from 'js/src/data/modals';
import { resumePausedSubscription } from 'js/src/data/pricing/resume-subscription-operations';
import { MAX_AI_VOICEOVER_WORD_COUNT } from 'js/src/data/script-composer';
import { trackUpsellClicked, trackUpsellDisplayed } from 'js/src/libs/analytics-utils';
import type { PrettyPlanName } from 'js/src/libs/consts';
import {
    FREE_PLAN_NAME,
    HUBSPOT_BOOK_CHAT_LINK,
    HUBSPOT_MEETING_SCRIPT_TAG_ID,
    HUBSPOT_MEETING_SCRIPT_TAG_SRC,
    PLACEHOLDER_IMAGE_URL,
    PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY,
} from 'js/src/libs/consts';
import { pluralize } from 'js/src/libs/custom-utils';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';

enum FEATURE_LOCK_CTA {
    VIEW_PRICING = 'VIEW_PRICING',
    CONTACT_US = 'CONTACT_US',
    CONFIRMATION = 'CONFIRMATION',
    // Only admins can open billing settings; everyone else gets CONFIRMATION instead.
    RESUME_SUBSCRIPTION = 'RESUME_SUBSCRIPTION',
}

enum FEATURE_LOCK_MODAL_STYLE {
    VERTICAL = 'VERTICAL',
    HORIZONTAL = 'HORIZONTAL',
}

type FEATURE_LOCK_DATA = {
    renderHeadline: (props: PropsFromRedux) => React.ReactNode;
    renderDescription: (props: PropsFromRedux) => React.ReactNode;
    image_url: string;
    call_to_action: FEATURE_LOCK_CTA;
    modal_style: FEATURE_LOCK_MODAL_STYLE;
    modal_size?: MODAL_SIZES;
    /**
     * When set, the modal shows this plan tier inline (with an in-app upgrade button)
     * instead of a "View pricing options" button that redirects to the pricing page.
     * Must be one of our active plan display names (matches `Plan.display_name`).
     */
    inline_plan_display_name?: PrettyPlanName;
    /**
     * Optional feature highlight to emphasize (bolded) at the top of the inline plan's
     * highlight list — used to draw attention to the specific feature this upsell is about.
     * Only applies when `inline_plan_display_name` is set.
     * - A string injects that text as a new emphasized highlight (e.g. "Premium AI image models").
     * - A number promotes the plan's existing highlight at that index to the top, so the copy
     *   stays in sync with the pricing page.
     */
    inline_plan_top_highlight?: string | number;
};

const FEATURE_LOCK_MODAL_CTA_TEXT: Record<FEATURE_LOCK_CTA, string> = {
    [FEATURE_LOCK_CTA.VIEW_PRICING]: 'View pricing options',
    [FEATURE_LOCK_CTA.CONTACT_US]: 'Contact us',
    [FEATURE_LOCK_CTA.CONFIRMATION]: 'Got it!',
    [FEATURE_LOCK_CTA.RESUME_SUBSCRIPTION]: 'Resume subscription',
};

const isFreePlanUser = () => SERVER.ANALYTICS?.plan_name === FREE_PLAN_NAME;
const isTeamAdmin = () => SERVER.USER.role === RoleEnum.ADMIN;

const FEATURE_LOCK_MODAL_DATA: Record<LOCKED_FEATURES, FEATURE_LOCK_DATA> = {
    /**
     * Resolution
     */
    [LOCKED_FEATURES.RENDER_720P]: {
        renderHeadline: () => 'Elevate your visuals',
        renderDescription: () => (
            <>
                Grab attention with razor-sharp videos at 720p resolution.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB8-ElevateVisuals.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.CAROUSEL_IMAGES]: {
        renderHeadline: () => 'Turn one video into a carousel',
        renderDescription: () => (
            <>
                Download every scene as a full-resolution image, ready to post as a carousel on
                LinkedIn, Instagram or X. Available on our <b>Basic plan</b> and up.
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB8-ElevateVisuals.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
    },
    [LOCKED_FEATURES.GIF_EXPORT]: {
        renderHeadline: () => 'Turn your video into a GIF',
        renderDescription: () => (
            <>
                Convert your video into an animated GIF, ready to drop into an email, chat or
                landing page. Available on our <b>Basic plan</b> and up.
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB8-ElevateVisuals.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
    },
    [LOCKED_FEATURES.RENDER_1080P]: {
        renderHeadline: () => 'Elevate your visuals',
        renderDescription: () => (
            <>
                Grab attention with razor-sharp videos at 1080p resolution. Available on our{' '}
                <b>Starter plan</b> and up.
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB8-ElevateVisuals.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_2,
        inline_plan_top_highlight: 1, // "1080p video resolution" highlight (index 1)
    },
    /**
     * Media
     */
    [LOCKED_FEATURES.UNLIMITED_PREMIUM_MEDIA]: {
        renderHeadline: () => 'You’re using premium media 💎',
        renderDescription: () => (
            <>
                You&apos;ve got good taste! Millions of premium photos and videos from Shutterstock{' '}
                <b>are available on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB1-UnlockPremiumMedia.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_2,
        inline_plan_top_highlight: 2, // "50M+ stock photos & videos" highlight (index 2)
    },
    [LOCKED_FEATURES.MARKETPLACE_MEDIA]: {
        renderHeadline: () => 'Access ultra-high quality media',
        renderDescription: () => (
            <>
                Get the best media available on the web—from both the Getty and Shutterstock
                catalogues. <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/Access-Ultra-High-Quality-Media_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_3,
        inline_plan_top_highlight: 1, // "500M stock photos & videos" highlight (index 1)
    },
    [LOCKED_FEATURES.HIT_MEDIA_CREDIT_LIMIT]: {
        renderHeadline: () => "You've run out of marketplace media credits",
        renderDescription: (props) => {
            const has_access_to_additional_credits =
                SERVER.USER.plan.toLowerCase().indexOf('business') >= 0 ||
                SERVER.USER.plan.toLowerCase().indexOf('team') >= 0;

            const premiumMediaCreditsLeft = props.modalContext?.premiumMediaCreditsLeft;
            if (has_access_to_additional_credits && typeof premiumMediaCreditsLeft === 'number') {
                return (
                    <>
                        You will have <b>{premiumMediaCreditsLeft}</b> marketplace media{' '}
                        {pluralize(premiumMediaCreditsLeft, 'credit')} remaining.{' '}
                        <a
                            href="https://help.lumen5.com/en/article/all-about-shutterstock-media-libraries-credits-1vexwek/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Learn&nbsp;more
                        </a>
                    </>
                );
            }

            return 'Upgrade your account to access more marketplace media';
        },
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/MarketplaceMediaCredits.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.HIT_IMAGE_TRANSFORM_MEDIA_CREDIT_LIMIT]: {
        renderHeadline: () => "You've run out of marketplace media credits",
        renderDescription: () =>
            'Upgrade your plan to access more credits and keep editing marketplace media with AI.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/MarketplaceMediaCredits.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Style: Watermark
     */
    [LOCKED_FEATURES.CUSTOM_WATERMARK]: {
        renderHeadline: () => 'Make your mark! ',
        renderDescription: () => (
            <>
                Increase brand awareness by adding a custom watermark to your videos.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB7-MakeYourMark.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_3,
        inline_plan_top_highlight: 2, // "Custom watermarks" highlight (index 2)
    },
    /**
     * Style: Fonts
     */
    [LOCKED_FEATURES.CUSTOM_FONT]: {
        renderHeadline: () => 'Upload custom fonts',
        renderDescription: () => (
            <>
                Need a specific font that&apos;s uniquely you?
                <br />
                Upgrade your plan to upload custom fonts.
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB6-UploadCustomFonts.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_3,
        inline_plan_top_highlight: 3, // "Upload your own fonts" highlight (index 3)
    },
    /**
     * Workspaces
     */
    [LOCKED_FEATURES.MULTIPLE_WORKSPACES]: {
        renderHeadline: () => 'Level up your workspace game!',
        renderDescription: () => (
            <>
                Elevate your workflow by creating separate workspaces for clients or projects.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal1-ElevateWorkspace.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_3,
        inline_plan_top_highlight: 'Multiple workspaces',
    },
    [LOCKED_FEATURES.HIT_MAX_WORKSPACES]: {
        renderHeadline: () => 'You’re keeping things busy!',
        renderDescription: () =>
            "Looks like you've reached your workspace limit - no worries though! Reach out to our team to create more workspaces.",
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal2-WorkspaceLimit.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Brand kits
     */
    [LOCKED_FEATURES.BRAND_KITS_LIMIT]: {
        renderHeadline: () => 'Effortlessly handle brand guidelines',
        renderDescription: () => (
            <>
                You&apos;ve hit your plan&apos;s brand kit limit. Manage multiple clients or brands
                with separate brand guidelines. <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal8-ManageBrandguidelines.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_3,
        inline_plan_top_highlight: 3, // "Multiple workspaces" highlight (index 3)
    },
    [LOCKED_FEATURES.BRAND_KITS_LIMIT_CONTACT_US]: {
        renderHeadline: () => 'Branding Extraordinaire Alert!',
        renderDescription: () =>
            "You've hit the 3 brand kits limit. Get in touch with our team to unlock more brand kits.",
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal8-ManageBrandguidelines.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },

    /**
     * Translation
     */
    [LOCKED_FEATURES.TRANSLATION]: {
        renderHeadline: () => 'Expand your reach with translations',
        renderDescription: () =>
            'Translate your videos into more languages to reach a global audience. More translations available on our Professional plan and up.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/website-assets/undraw_around_the_world_re_rb1p.svg',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_3,
        inline_plan_top_highlight: 'Video translations',
    },

    /**
     * AI Voiceover
     */
    [LOCKED_FEATURES.GENERATE_SCRIPT_CONTROLS]: {
        renderHeadline: () => 'Unlock our most advanced AI',
        renderDescription: () => (
            <div className="m-l5-0">
                Refine your storytelling with more accurate and controlled AI scripts.
                <div>
                    <div className="mt-l5-16">
                        <b>AI features you get when you upgrade</b>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i
                            translate="no"
                            className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8"
                        >
                            tune
                        </i>
                        <span>
                            Craft polished video scripts with enhanced <b>duration</b> and{' '}
                            <b>editing controls.</b>
                        </span>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i
                            translate="no"
                            className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8"
                        >
                            record_voice_over
                        </i>
                        <span>
                            Access <b>40+ lifelike AI Voices</b> and{' '}
                            <b>up to {MAX_AI_VOICEOVER_WORD_COUNT} words</b> for longer videos.
                        </span>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i
                            translate="no"
                            className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8"
                        >
                            photo_library
                        </i>
                        <span>
                            Create videos faster, with <b>better media</b> and{' '}
                            <b>more varied scene recommendations</b>
                        </span>
                    </div>
                </div>
            </div>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal9-UnlockAdvancedAI%20-%20CROPPED.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
        inline_plan_top_highlight: 'Advanced AI script controls',
    },
    [LOCKED_FEATURES.AI_VOICEOVER_MINUTES_LIMIT]: {
        renderHeadline: () => "You've run out of AI voiceover minutes for this cycle",
        renderDescription: () => (
            <div>
                Additional AI voiceover minutes <b>are available on an upgraded plan.</b>
            </div>
        ),
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/Video-Minutes_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.SUBSCRIPTION_PAUSED]: {
        renderHeadline: () => 'Your subscription is paused',
        renderDescription: () =>
            isTeamAdmin() ? (
                <div>
                    Resume your subscription to publish and download videos again. Your card on file
                    will be charged today, and monthly billing restarts from today.{' '}
                    <a href={Urls.BILLING_SETTINGS} target="_blank" rel="noreferrer">
                        Manage billing
                    </a>
                </div>
            ) : (
                'Ask a team admin to resume your subscription to publish and download videos again.'
            ),
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/Video-Minutes_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.RESUME_SUBSCRIPTION,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.MAX_VIDEO_DURATION_LIMIT]: {
        renderHeadline: () => 'Your video is too long',
        renderDescription: () => (
            <div>
                Create videos longer than 2 minutes <b>on an upgraded plan.</b>
            </div>
        ),
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/Video-Minutes_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
        inline_plan_top_highlight: 2, // "Max 10 minute long videos" highlight (index 2)
    },
    /**
     * Teams
     */
    [LOCKED_FEATURES.TEAM_MEMBERS_ENTERPRISE]: {
        renderHeadline: () => 'Add more team members',
        renderDescription: () =>
            'Looks like your team is growing! Contact your account executive to add more team members.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal6-AddTeamMembers.png',
        call_to_action: FEATURE_LOCK_CTA.CONFIRMATION,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.TEAM_MEMBERS]: {
        renderHeadline: () => 'Introduce us to your team!',
        renderDescription: () =>
            'Our team features make it easy to scale up video creation and collaborate. Contact us to check out our Enterprise plans!',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal6-AddTeamMembers.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.TEAM_MEMBERS_LIMIT]: {
        renderHeadline: () => 'Elevate your team’s features with our Enterprise plan',
        renderDescription: () =>
            'Invite your team and unlock advanced controls like roles, approvals, SSO, and more!',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal6-AddTeamMembers.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Dashboard
     */
    [LOCKED_FEATURES.VIDEO_LIMIT]: {
        renderHeadline: () => "You've reached your free video limit",
        renderDescription: () =>
            'Free accounts can create up to 5 videos per month. Creating more videos is possible on an upgraded plan.',
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/No-Videos_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
        inline_plan_top_highlight: 'Unlimited videos',
    },
    [LOCKED_FEATURES.CANT_PUBLISH_AI_AVATAR]: {
        renderHeadline: () => 'Your video contains an AI avatar',
        renderDescription: () => (
            <>
                <b>To publish your video, please upgrade your account</b> or remove your AI avatar.
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB11-UpgradetoPublishAIAvatar.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
        inline_plan_top_highlight: 'AI avatars',
    },
    [LOCKED_FEATURES.AI_AVATAR_SCRIPT_COMPOSER]: {
        renderHeadline: () => 'This is a premium feature',
        renderDescription: () => (
            <>
                Your account currently offers free, preview-only access to AI avatars.{' '}
                <b>Publishing videos with AI avatars is available on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB12-UpgradetoAccessAIAvatar.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
        inline_plan_top_highlight: 'AI avatars',
    },
    /**
     * Audio Enhancement
     */
    [LOCKED_FEATURES.AUDIO_ENHANCEMENT]: {
        renderHeadline: () => 'Clean up and enhance your audio',
        renderDescription: () => (
            <>
                Remove background noise and enhance speech clarity with our audio enhancement.{' '}
                <b>This feature is available on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal-AudioEnhancement.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.IMAGE_BACKGROUND_REMOVAL]: {
        renderHeadline: () => 'Unlock easier ways to edit',
        renderDescription: () => (
            <>Transform any image into a flexible creative asset with background removal.</>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal-UnlockMediaEdit.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Voice Cloning
     */
    [LOCKED_FEATURES.VOICE_CLONING_LIMIT]: {
        renderHeadline: () => {
            return isFreePlanUser() ? 'Want to be heard?' : 'Want more voices?';
        },
        renderDescription: () => {
            if (isFreePlanUser()) {
                return (
                    <>
                        Skip recording voiceovers every time. Create AI voices from your recordings.{' '}
                        <b>Available on the Basic plan and up.</b>
                    </>
                );
            }
            const maxVoiceClones = SERVER.USER.capabilities.max_voice_clones;
            return (
                <>
                    You've reached the {maxVoiceClones} {pluralize(maxVoiceClones, 'voice')} limit
                    on your current plan. Upgrade your plan to create and use more AI voice clones.
                </>
            );
        },
        image_url:
            'https://storage.googleapis.com/lumen5-site-images/website-assets/two-boombox-tongues.svg',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.AI_IMAGE_GENERATION_MODELS]: {
        renderHeadline: () => 'Upgrade to access premium AI media generation',
        renderDescription: () => 'Available on our Basic plan and up.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal9-UnlockAdvancedAI%20-%20CROPPED.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
        inline_plan_top_highlight: 'Premium AI image models',
    },
    [LOCKED_FEATURES.ANIMATION_MINUTES]: {
        renderHeadline: () => 'Upgrade to access animated explainer videos',
        renderDescription: () => 'Available on our Basic plan and up.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/freeminA.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
        inline_plan_top_highlight: 5,
    },
    [LOCKED_FEATURES.GENX_SCRIPT_DURATION]: {
        renderHeadline: () => 'Create longer animated videos',
        renderDescription: () => (
            <>
                Free accounts are limited to 30-second scripts. Generate and paste longer scripts to
                create longer animated videos <b>on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/freeminA.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
    },
    [LOCKED_FEATURES.FREE_USER_GENX_IMAGE_LIMIT]: {
        renderHeadline: () => "You've reached your free image generation limit",
        renderDescription: () => (
            <>
                Free accounts can generate a limited number of animated explainer images each month.
                Keep creating <b>on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/freeminA.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
        inline_plan_display_name: PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY.UPGRADE_LEVEL_1,
    },
    // No inline plan card here: this modal is shown to users on any paid tier, and the
    // inline card's target plan is a single static tier that can't fit all of them.
    [LOCKED_FEATURES.PAID_GENX_IMAGE_LIMIT]: {
        renderHeadline: () => "You've run out of premium image generations",
        renderDescription: () => (
            <>
                Your plan includes a set number of premium image generations each billing cycle.
                Keep creating with a higher limit <b>on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/freeminA.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
    },
};

type PropsFromRedux = ConnectedProps<typeof connector>;

function featureInFeatureLockData(modalContext: Readonly<FeatureLockModalContext> | null) {
    const featureName = modalContext?.featureName;
    if (!featureName) return null;
    return !!FEATURE_LOCK_MODAL_DATA[featureName];
}

function getFieldFromFeatureLockData<K extends keyof FEATURE_LOCK_DATA>(
    modalContext: Readonly<FeatureLockModalContext> | null,
    field_name: K,
    default_value: FEATURE_LOCK_DATA[K],
): FEATURE_LOCK_DATA[K] {
    if (!featureInFeatureLockData(modalContext)) {
        return default_value;
    }
    const featureName = modalContext?.featureName;
    if (!featureName) throw new Error('Feature name is required to show the feature lock modal');
    return FEATURE_LOCK_MODAL_DATA[featureName][field_name] ?? default_value;
}

const getAnalyticsContext = (modalContext: Readonly<FeatureLockModalContext> | null) => {
    return `${modalContext?.featureName} feature lock modal`;
};

const FeatureLockModalPresentation = React.memo(function FeatureLockModalPresentation({
    modalContext,
    showing,
    hideModal,
}: PropsFromRedux) {
    const [showHubspotMeetingModal, setShowHubspotMeetingModal] = React.useState(false);

    const getHeadline = React.useCallback(() => {
        const renderHeadline = getFieldFromFeatureLockData(
            modalContext,
            'renderHeadline',
            () => '',
        );
        return renderHeadline({
            modalContext,
            showing,
            hideModal,
        });
    }, [modalContext, showing, hideModal]);

    const getDescription = React.useCallback(() => {
        const renderDescription = getFieldFromFeatureLockData(
            modalContext,
            'renderDescription',
            () => '',
        );
        return renderDescription({
            modalContext,
            showing,
            hideModal,
        });
    }, [modalContext, showing, hideModal]);

    const getImageUrl = React.useCallback(() => {
        return getFieldFromFeatureLockData(modalContext, 'image_url', PLACEHOLDER_IMAGE_URL);
    }, [modalContext]);

    const getCallToAction = React.useCallback(() => {
        const callToAction = getFieldFromFeatureLockData(
            modalContext,
            'call_to_action',
            FEATURE_LOCK_CTA.VIEW_PRICING,
        );
        if (callToAction === FEATURE_LOCK_CTA.RESUME_SUBSCRIPTION && !isTeamAdmin()) {
            return FEATURE_LOCK_CTA.CONFIRMATION;
        }
        return callToAction;
    }, [modalContext]);

    const getModalStyle = React.useCallback(() => {
        return getFieldFromFeatureLockData(
            modalContext,
            'modal_style',
            FEATURE_LOCK_MODAL_STYLE.VERTICAL,
        );
    }, [modalContext]);

    const getModalSize = React.useCallback(() => {
        return getFieldFromFeatureLockData(modalContext, 'modal_size', MODAL_SIZES.DEFAULT);
    }, [modalContext]);

    // When the inline plan can't be resolved (e.g. plans failed to load), fall back to
    // the standard "View pricing options" button rather than leaving the user stuck.
    // Keyed by feature name so the fallback resets only when a different feature is shown.
    const featureName = modalContext?.featureName ?? null;
    const [inlinePlanUnavailableFor, setInlinePlanUnavailableFor] = React.useState<
        LOCKED_FEATURES | null | undefined
    >(null);
    const inlinePlanUnavailable = !!featureName && inlinePlanUnavailableFor === featureName;
    const onInlinePlanUnavailable = React.useCallback(() => {
        setInlinePlanUnavailableFor(featureName);
    }, [featureName]);

    const inlinePlanDisplayName = getFieldFromFeatureLockData(
        modalContext,
        'inline_plan_display_name',
        undefined,
    );
    const inlinePlanTopHighlight = getFieldFromFeatureLockData(
        modalContext,
        'inline_plan_top_highlight',
        undefined,
    );
    const showInlinePlan = !!inlinePlanDisplayName && !inlinePlanUnavailable;

    const secondaryCtaText = modalContext?.secondaryCtaText;
    const onClickSecondaryCta = modalContext?.onClickSecondaryCta;

    const getHubSpotMeetingLink = React.useCallback(() => {
        const featureName = modalContext?.featureName;
        if (
            featureName === LOCKED_FEATURES.TEAM_MEMBERS ||
            featureName === LOCKED_FEATURES.TEAM_MEMBERS_LIMIT ||
            featureName === LOCKED_FEATURES.TEAM_MEMBERS_ENTERPRISE
        ) {
            return 'https://meetings.hubspot.com/lumen5/learn-more-about-team-features';
        }
        return HUBSPOT_BOOK_CHAT_LINK;
    }, [modalContext]);

    const onHidden = React.useCallback(() => {
        if (modalContext) {
            const onClose = modalContext.onClose;
            if (typeof onClose === 'function') {
                onClose();
            }
        }
        hideModal(); // actually close the modal via redux
    }, [modalContext, hideModal]);

    const toggleHubspotMeetingModalVisibility = React.useCallback(
        (isVisible = false) => {
            onHidden();
            setShowHubspotMeetingModal(isVisible);

            if (isVisible) {
                // open hubspot meeting modal
                let scriptTag = document.getElementById(
                    HUBSPOT_MEETING_SCRIPT_TAG_ID,
                ) as HTMLScriptElement;

                if (scriptTag) {
                    scriptTag.remove();
                }
                scriptTag = document.createElement('script');
                scriptTag.setAttribute('id', HUBSPOT_MEETING_SCRIPT_TAG_ID);
                scriptTag.src = HUBSPOT_MEETING_SCRIPT_TAG_SRC;
                scriptTag.nonce = window._CSP_NONCE;
                document.body.appendChild(scriptTag);

                analytics.track('Let_chat');
            }
        },
        [onHidden],
    );

    const [isResumingSubscription, setIsResumingSubscription] = React.useState(false);

    const resumeSubscription = React.useCallback(async () => {
        setIsResumingSubscription(true);
        const outcome = await resumePausedSubscription();
        setIsResumingSubscription(false);
        if (outcome.status === 'resumed') {
            Toast.success('Your subscription is resumed. You can publish your video now.');
            onHidden();
        } else if (outcome.status === 'payment_failed') {
            Toast.error(outcome.message);
        } else if (outcome.status === 'payment_pending') {
            Toast.info(outcome.message);
        } else {
            Toast.error("We couldn't resume your subscription. Please try again.");
        }
    }, [onHidden]);

    const ctaClicked = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const ctaHandlers: Record<FEATURE_LOCK_CTA, () => void> = {
                [FEATURE_LOCK_CTA.VIEW_PRICING]: () => {
                    window.open(Urls.PRICING, '_blank');
                },
                [FEATURE_LOCK_CTA.CONTACT_US]: () => {
                    toggleHubspotMeetingModalVisibility(true);
                },
                [FEATURE_LOCK_CTA.CONFIRMATION]: onHidden,
                [FEATURE_LOCK_CTA.RESUME_SUBSCRIPTION]: () => {
                    void resumeSubscription();
                },
            };

            const func = ctaHandlers[getCallToAction()];

            func();

            const context = getAnalyticsContext(modalContext);
            trackUpsellClicked(context, {
                subCategory: modalContext?.subCategory,
            });
        },
        [
            getCallToAction,
            toggleHubspotMeetingModalVisibility,
            onHidden,
            resumeSubscription,
            modalContext,
        ],
    );

    const comparePlansClicked = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(Urls.PRICING, '_blank');
            const context = getAnalyticsContext(modalContext);
            trackUpsellClicked(context, {
                subCategory: modalContext?.subCategory,
            });
        },
        [modalContext],
    );

    const renderContent = React.useCallback(() => {
        const descriptionNode = getDescription();
        if (getModalStyle() === FEATURE_LOCK_MODAL_STYLE.HORIZONTAL) {
            if (showInlinePlan && inlinePlanDisplayName) {
                return (
                    <div className="m-l5-0 px-l5-16 mx-l5-8">
                        <div className="d-flex flex-column flex-lg-row align-items-center">
                            <div
                                className={`${styles['text-block']} ${styles['inline-plan-text-block']} d-flex flex-column`}
                            >
                                <img
                                    className={`${styles['inline-plan-feature-image']} object-fit-contain mb-l5-8`}
                                    src={getImageUrl()}
                                    alt="Feature preview"
                                    loading="lazy"
                                />
                                <h4 className="font-l5 font-l5--h4 mb-l5-8">{getHeadline()}</h4>
                                {descriptionNode && (
                                    <div
                                        className={`${styles['description']} font-l5 font-l5--small color-grey-800`}
                                    >
                                        {descriptionNode}
                                    </div>
                                )}
                                {secondaryCtaText && (
                                    <PrimaryButton
                                        className="mt-l5-8 align-self-center"
                                        text={secondaryCtaText}
                                        variant={BUTTON_VARIANTS.LINK}
                                        onClick={onClickSecondaryCta}
                                    />
                                )}
                            </div>
                            <div className={styles['inline-plan-card']}>
                                <FeatureLockInlinePlan
                                    planDisplayName={inlinePlanDisplayName}
                                    topHighlight={inlinePlanTopHighlight}
                                    onPlanUnavailable={onInlinePlanUnavailable}
                                />
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <div className="m-l5-0 px-l5-16 mx-l5-8 mb-l5-48">
                    <div className="d-flex flex-row">
                        <div className={styles['text-block']}>
                            <h4 className="mt-l5-8 mb-l5-4">{getHeadline()}</h4>
                            <div className={`${styles['description']} color-grey-800 mb-l5-32`}>
                                {descriptionNode}
                            </div>
                        </div>
                        <div
                            className={`${styles['image']} d-flex align-items-center justify-content-center`}
                        >
                            <img
                                className="w-100 object-fit-contain"
                                src={getImageUrl()}
                                alt="Feature preview"
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <PrimaryButton
                        text={FEATURE_LOCK_MODAL_CTA_TEXT[getCallToAction()]}
                        onClick={ctaClicked}
                        disabled={isResumingSubscription}
                    />
                    {secondaryCtaText && (
                        <PrimaryButton
                            className="mt-l5-16"
                            text={secondaryCtaText}
                            variant={BUTTON_VARIANTS.LINK}
                            onClick={onClickSecondaryCta}
                        />
                    )}
                </div>
            );
        } else {
            return (
                <div className="d-flex flex-column align-items-center mb-l5-24">
                    <div className={`${styles['image']} d-flex align-items-center`}>
                        <img src={getImageUrl()} alt="Feature preview" loading="lazy" />
                    </div>
                    <h4 className="mt-l5-8 mb-l5-4 text-center">{getHeadline()}</h4>
                    <div className={`${styles['description']} color-grey-800 text-center mb-l5-32`}>
                        {descriptionNode}
                    </div>
                    <PrimaryButton
                        text={FEATURE_LOCK_MODAL_CTA_TEXT[getCallToAction()]}
                        onClick={ctaClicked}
                        disabled={isResumingSubscription}
                    />
                    {secondaryCtaText && (
                        <PrimaryButton
                            className="mt-l5-16"
                            text={secondaryCtaText}
                            variant={BUTTON_VARIANTS.LINK}
                            onClick={onClickSecondaryCta}
                        />
                    )}
                </div>
            );
        }
    }, [
        getDescription,
        getHeadline,
        getModalStyle,
        getImageUrl,
        getCallToAction,
        ctaClicked,
        isResumingSubscription,
        showInlinePlan,
        inlinePlanDisplayName,
        inlinePlanTopHighlight,
        onInlinePlanUnavailable,
        secondaryCtaText,
        onClickSecondaryCta,
    ]);

    // Track upsell_displayed when modal becomes visible
    React.useEffect(() => {
        if (showing) {
            trackUpsellDisplayed(getAnalyticsContext(modalContext), {
                subCategory: modalContext?.subCategory,
            });
        }
    }, [showing, modalContext]);

    return (
        <>
            <Modal
                showing={showing}
                size={getModalSize()}
                onHidden={onHidden}
                className={classNames(styles['feature-lock-modal'], {
                    [styles['vertical']]: getModalStyle() === FEATURE_LOCK_MODAL_STYLE.VERTICAL,
                    [styles['horizontal']]: getModalStyle() === FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
                })}
                footer={
                    showInlinePlan ? (
                        <div className="d-flex justify-content-center w-100">
                            <PrimaryButton
                                text="Compare all plans & features"
                                variant={BUTTON_VARIANTS.LINK}
                                onClick={comparePlansClicked}
                            />
                        </div>
                    ) : undefined
                }
            >
                {showing && renderContent()}
            </Modal>
            {/* Hubspot Meeting Schedule Modal */}
            <Modal
                showing={showHubspotMeetingModal}
                size={MODAL_SIZES.LG}
                title={'Schedule a time with us'}
                onHidden={() => {
                    toggleHubspotMeetingModalVisibility(false);
                }}
            >
                <div
                    className="meetings-iframe-container"
                    data-src={`${getHubSpotMeetingLink()}?embed=true`}
                ></div>
            </Modal>
        </>
    );
});

const mapStateToProps = (state: RootState) => ({
    modalContext: getModalContext<FeatureLockModalContext>(state),
    showing: isFeatureLockModalShowing(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            hideModal,
        },
        dispatch,
    );
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const FeatureLockModal = connector(FeatureLockModalPresentation);

export default FeatureLockModal;
